import { convert } from "./convert.js";
import { extract } from "./extract.js";
import { translate } from "./translate.js";
import { ClingoStore, loadSearchSpace } from "./fetchClingo.js"
import { search_asp, deepcopy } from "./util.js";
import { LossCompare, mergeLoss, default_cost, normalize } from "./loss/evaluate.js";
import { clearVirtual } from "./virtual.js";
import { makeLLTConstraints, makeMLTConstraints } from "./config.js";

// recommender interface
const LP_big = [
  "big.lp",
  "context.lp",
  "constraints.lp",
  "cost.lp",
  "definition.lp",
  "detection.lp",
  "infer.lp",
  "output.lp",
  "lock.lp",
  "rationale.lp"
];
let LP_big_loaded;
const LP_small = [
  "small.lp",
  "context.lp",
  "constraints.lp",
  "cost.lp",
  "definition.lp",
  "detection.lp",
  "infer.lp",
  "output.lp",
  "lock.lp",
  "rationale.lp"
];
let LP_small_loaded;
async function run(source, constraints = {}, config = {}) {
  // for big models
  // @ source: source view fronto spec
  // @ constraints: user inputs !! object {name: vlaue}
  // @ config: recommender configuration
  // config.clingo: clingo-related configs
  // config.recommender: recommender configs

  let start_time = new Date();
  // 1. extract features (A)
  let extracted = await extract(source, constraints, config.recommender);
  let evaluator = new LossCompare(source);
  let lltConstraints = makeLLTConstraints(config.recommender.lessLikeThisStack);
  await evaluator.makeSource();
  // 2. pass to big changes (B)
  let clingoObj = new ClingoStore(config.clingo, config.clingoObject);
  clingoObj.addStatements(extracted.feature_statements);
  clingoObj.addStatements(lltConstraints);
  let id_mappings = extracted.id_mappings;
  if (!LP_big_loaded) {
    LP_big_loaded = await loadSearchSpace(LP_big);
  }
  clingoObj.addSearchSpaceDefinitions(LP_big_loaded);
  clingoObj.addStatements(["context(big)."])
  if (config.recommender.ignore_minmize_transformation) {
    clingoObj.addStatements(["detect(ignore_minmize_transformation)."])
  }
  let output_config = {
    suggestion_minimized: false,
    for_printing: config.recommender.intended_screen === "printing",
    for_tiny: ["thumbnail", "watch"].includes(config.recommender.intended_screen)
  };
  let bigModels = await clingoObj.fetch();
  let bigItems_original = bigModels.map(d => { return { model: d } });
  
  // 3. convert to cicero (C)
  for (const bi in bigItems_original) {
    let item = bigItems_original[bi];
    let translated = await translate(item.model, source, id_mappings, config);
    item.rules = translated.translated;
    item.rules.name = item.rules.name + "-" + bi.toString();
    item.parsed = translated.parsed;
    item.descriptions = translated.descriptions;
    if (search_asp(item.parsed, "detect(minimize_transformation", "startsWith")?.length > 0) {
      output_config.suggestion_minimized = true;
    }
  }
  if (bigItems_original.length > 3) {
    output_config.suggestion_minimized = false;
  }
  // 4. convert to fronto (D)
  for (const item of bigItems_original) {
    item.spec = await convert(source, item.rules, config);
  }
  bigItems_original = bigItems_original.sort((a, b) => { return a.parsed.cost - b.parsed.cost });
  let bigItems;
  if (bigItems_original.length <= config.recommender.n_big_models) {
    bigItems = bigItems_original
  } else if (config.recommender.p_drastic !== undefined) {
    let n_drastic = Math.round(config.recommender.n_big_models * config.recommender.p_drastic);
    let n_simple = config.recommender.n_big_models - n_drastic;
    let bigItems_drastic = bigItems_original.slice(bigItems_original.length - n_drastic, bigItems_original.length);
    let bigItems_simple = bigItems_original.slice(0, n_simple);
    bigItems = [];
    bigItems.push(...bigItems_drastic);
    bigItems.push(...bigItems_simple);
  } else {
    bigItems = bigItems_original.slice(0, config.recommender.n_big_models);
  }
  // 5. post-processing before evaluation
  let itemsTotal = [];
  for (const bi in bigItems) {
    let big = bigItems[bi];
    let item = {};
    item.parsed_big = big.parsed;
    item.parsed_small = {};
    item.big_model = big.model;
    item.small_model = {};
    item.descriptions = big.descriptions;
    item.rules = big.rules;
    item.spec = await convert(source, big.rules, config);
    item.interaction_added = [];
    search_asp(big.parsed, "do(add_zoom_to_map", "startsWith")?.forEach(p => {
      item.interaction_added.push("zoom");
    });
    search_asp(big.parsed, "do(add_context_to_bar", "startsWith")?.forEach(p => {
      item.interaction_added.push("context");
    });
    search_asp(item.parsed_small, "do(add_tooltip", "startsWith")?.forEach(p => {
      item.interaction_added.push("tooltip");
    });
    search_asp(item.parsed_small, "do(fix_tooltip", "startsWith")?.forEach(p => {
      item.interaction_added.push("tooltip_fix");
    });
    itemsTotal.push(item);
  }
  // 9. Evaluation
  let evaluation = await evaluator.compare(itemsTotal.map((item) => item.spec), config.pyodide);
  let eval_tab = [];
  evaluation.result?.forEach((loss) => {
    let merged = mergeLoss(loss, config.weight || default_cost);
    loss.loss = merged.val;
    loss.aggregate = merged.aggregate;
    eval_tab.push(merged.tab);
  });
  // normalize
  let normalized_eval = await normalize(eval_tab);
  itemsTotal.forEach((item, i) => {
    item.raw_loss = evaluation.result[i];
    item.norm_loss = normalized_eval[i];
  });
  itemsTotal.sort((a, b) => {
    return (
      a.is_non_rec ? -1 :
        b.is_non_rec ? 1 :
          a.norm_loss.val - b.norm_loss.val
    );
  });
  // Return
  clearVirtual();
  return { items: itemsTotal, evaluator, output_config, time_taken: (new Date() - start_time) };
}

async function run_small(source, evaluator, big, constraints = {}, config = {}) {
  let start_time = new Date();
  // 1. Extraction (A)
  let extracted = await extract(big.spec, constraints, config.recommender);
  if (!evaluator) {
    evaluator = new LossCompare(source);
    await evaluator.makeSource();
  }
  let lltConstraints = makeLLTConstraints(config.recommender.lessLikeThisStack);
  let mltConstraints = makeMLTConstraints(constraints.observed || []);
  let output_config = {
    suggestion_minimized: false,
    for_printing: config.recommender.intended_screen === "printing",
    for_tiny: ["thumbnail", "watch"].includes(config.recommender.intended_screen)
  };
  // 2. pass to small changes (B)
  let clingoObj = new ClingoStore(config.clingo, config.clingoObject);
  clingoObj.addStatements(extracted.feature_statements);
  clingoObj.addStatements(lltConstraints);
  clingoObj.addStatements(mltConstraints);
  if (constraints.small?.length > 0) clingoObj.addStatements(constraints.small);
  let id_mappings = extracted.id_mappings;
  if (!LP_small_loaded) {
    LP_small_loaded = await loadSearchSpace(LP_small);
  }
  clingoObj.addSearchSpaceDefinitions(LP_small_loaded);
  clingoObj.addStatements(["context(small)."]);
  let n_small_models = config.recommender.n_small_models
  // maintaining the transformation context
  search_asp(big.parsed, "detect(large_to_small,", "startsWith")?.forEach(p => {
    clingoObj.addStatements([p.asp]);
  });
  search_asp(big.parsed, "detect(small_to_large,", "startsWith")?.forEach(p => {
    clingoObj.addStatements([p.asp]);
  });
  // preventing no-transformation suggestion for small changes
  clingoObj.addStatements("illegal(no_strategy) :- 0 = { small(_,_,_) }.")
  let smallModels = await clingoObj.fetch({ models: 20 });
  let smallItems_original = smallModels.map(d => { return { small_model: d, big_model: deepcopy(big.big_model), descriptions: deepcopy(big.descriptions) } })
  // 3. convert to cicero (C)
  for (const si in smallItems_original) {
    let item = smallItems_original[si];
    let translated = await translate(item.small_model, big.spec, id_mappings, config);
    item.rules = translated.translated;
    item.rules.name = item.rules.name + "-" + si.toString();
    item.parsed_small = translated.parsed;
    item.parsed_big = big.parsed;
    item.descriptions.push(...translated.descriptions);
    if (search_asp(item.parsed_small, "detect(minimize_transformation", "startsWith")?.length > 0) {
      output_config.suggestion_minimized = true;
    }
  }
  if (smallItems_original.length > 3) {
    output_config.suggestion_minimized = false;
  }
  let smallItems;
  smallItems_original = smallItems_original.sort((a, b) => { return a.parsed_small.cost - b.parsed_small.cost });
  if (smallItems_original.length <= n_small_models) {
    smallItems = smallItems_original
  } else if (config.recommender.p_drastic !== undefined) {
    let n_drastic = Math.round(n_small_models * config.recommender.p_drastic);
    let n_simple = n_small_models - n_drastic;
    let smallItems_drastic = smallItems_original.slice(smallItems_original.length - n_drastic, smallItems_original.length);
    let smallItems_simple = smallItems_original.slice(0, n_simple);
    smallItems = [];
    smallItems.push(...smallItems_drastic);
    smallItems.push(...smallItems_simple);
  } else {
    smallItems = smallItems_original.slice(0, n_small_models);
  }
  // 4. combine cicero & evaluation
  for (const item of smallItems) {
    let rule = deepcopy(big.rules);
    rule.name = item.rules.name;
    rule.description = item.rules.description;
    rule.metadata = item.rules.metadata;
    rule.transformations.push(...item.rules.transformations);
    item.interaction_added = [];
    search_asp(item.parsed_small, "do(add_tooltip", "startsWith")?.forEach(p => {
      item.interaction_added.push("tooltip");
    });
    search_asp(item.parsed_small, "do(fix_tooltip", "startsWith")?.forEach(p => {
      item.interaction_added.push("tooltip_fix");
    });
    item.rules = rule;
    // 8-a. Convert to Fronto (D) for evaluation
    item.spec = await convert(source, item.rules, config);
  }

  // evaluation
  let evaluation = await evaluator.compare(smallItems.map((item) => item.spec), config.pyodide);
  let eval_tab = [];
  evaluation.result.forEach((loss) => {
    let merged = mergeLoss(loss, config.weight || default_cost);
    loss.loss = merged.val;
    loss.aggregate = merged.aggregate;
    eval_tab.push(merged.tab);
  });
  // normalize
  let normalized_eval = await normalize(eval_tab);
  smallItems.forEach((item, i) => {
    item.raw_loss = evaluation.result[i];
    item.norm_loss = normalized_eval[i];
  });
  smallItems.sort((a, b) => { return a.norm_loss.val - b.norm_loss.val; });
  clearVirtual();
  return { items: smallItems, evaluator, time_taken: (new Date() - start_time), output_config };
}

export { run, run_small };